import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft,
  CircleNotch,
  Plus,
  Trash,
  Download,
  PaperPlaneTilt,
  CheckCircle,
  PencilSimple,
  Receipt,
  XCircle,
  CurrencyDollar
} from '@phosphor-icons/react';


const PAYMENT_TERMS_OPTIONS = [
  'Net 15',
  'Net 30',
  'Net 45',
  'Net 60',
  'Due on Receipt',
  '50% Deposit + 50% on Completion'
];

function InvoiceStatusBadge({ status }) {
  const styles = {
    'Draft': 'bg-slate-100 text-slate-700',
    'Sent': 'bg-blue-100 text-blue-700',
    'Paid': 'bg-green-100 text-green-700',
    'Overdue': 'bg-red-100 text-red-700',
    'Cancelled': 'bg-slate-100 text-slate-500 line-through',
  };

  const labels = {
    'Draft': 'Borrador',
    'Sent': 'Enviada',
    'Paid': 'Pagada',
    'Overdue': 'Vencida',
    'Cancelled': 'Cancelada',
  };

  return (
    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${styles[status] || 'bg-slate-100 text-slate-700'}`}
          data-testid="invoice-status-badge">
      {labels[status] || status}
    </span>
  );
}

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [invoice, setInvoice] = useState({
    client_id: '',
    items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
    tax_percent: 0,
    payment_terms: 'Net 30',
    due_days: 30,
    notes: '',
    language: 'ES'
  });
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState('');

  useEffect(() => {
    fetchClients();
    if (isNew) {
      fetchQuotes();
    }
    if (!isNew) {
      fetchInvoice();
    }
  }, [id, isNew]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/quotes`, { withCredentials: true });
      setQuotes(response.data.filter(q => ['Approved', 'Sent', 'Signed', 'Paid'].includes(q.status)));
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${id}`, { withCredentials: true });
      setInvoice(response.data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromQuote = async () => {
    if (!selectedQuoteId) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/invoices/from-quote/${selectedQuoteId}`, {}, {
        withCredentials: true
      });
      navigate(`/invoices/${response.data.id}`);
    } catch (error) {
      console.error('Error creating invoice from quote:', error);
      alert(error.response?.data?.detail || 'Error al crear factura desde cotizacion');
    } finally {
      setSaving(false);
    }
  };

  const calculateItemTotal = (item) => item.quantity * item.unit_price;
  const calculateSubtotal = () => invoice.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  const calculateTax = () => calculateSubtotal() * (invoice.tax_percent / 100);
  const calculateTotal = () => calculateSubtotal() + calculateTax();

  const handleItemChange = (index, field, value) => {
    const newItems = [...invoice.items];
    newItems[index] = {
      ...newItems[index],
      [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value
    };
    newItems[index].total = calculateItemTotal(newItems[index]);
    setInvoice({ ...invoice, items: newItems });
  };

  const addItem = () => {
    setInvoice({
      ...invoice,
      items: [...invoice.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    if (invoice.items.length === 1) return;
    setInvoice({ ...invoice, items: invoice.items.filter((_, i) => i !== index) });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!invoice.client_id) {
      alert('Selecciona un cliente');
      return;
    }
    if (!invoice.items.some(item => item.description && item.quantity > 0)) {
      alert('Agrega al menos un servicio');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...invoice,
        items: invoice.items.map(item => ({ ...item, total: calculateItemTotal(item) }))
      };

      if (isNew) {
        const response = await axios.post(`${API_URL}/api/invoices`, payload, { withCredentials: true });
        navigate(`/invoices/${response.data.id}`);
      } else {
        await axios.put(`${API_URL}/api/invoices/${id}`, payload, { withCredentials: true });
        setEditing(false);
        fetchInvoice();
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Error al guardar la factura');
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API_URL}/api/invoices/${id}/email`, {}, { withCredentials: true });
      alert('Factura enviada por email exitosamente');
      fetchInvoice();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error al enviar el email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMarkPaid = async () => {
    try {
      await axios.post(`${API_URL}/api/invoices/${id}/mark-paid`, {}, { withCredentials: true });
      fetchInvoice();
    } catch (error) {
      console.error('Error marking as paid:', error);
      alert('Error al marcar como pagada');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('¿Estas seguro de cancelar esta factura?')) return;
    try {
      await axios.post(`${API_URL}/api/invoices/${id}/cancel`, {}, { withCredentials: true });
      fetchInvoice();
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      alert('Error al cancelar la factura');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${id}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factura-${invoice.invoice_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estas seguro de eliminar esta factura?')) return;
    try {
      await axios.delete(`${API_URL}/api/invoices/${id}`, { withCredentials: true });
      navigate('/invoices');
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Error al eliminar la factura');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="invoice-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/invoices')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? 'Nueva Factura' : editing ? 'Editar Factura' : invoice.invoice_number}
          </h1>
          {!isNew && !editing && (
            <div className="flex items-center gap-2 mt-1">
              <InvoiceStatusBadge status={invoice.status} />
              {invoice.client_name && (
                <span className="text-slate-500">| {invoice.client_name}</span>
              )}
            </div>
          )}
        </div>
        {!isNew && !editing && (
          <div className="flex items-center gap-2 flex-wrap">
            {invoice.status === 'Draft' && (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                  data-testid="edit-invoice-button"
                >
                  <PencilSimple size={18} />
                  Editar
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  data-testid="send-invoice-email-button"
                >
                  {sendingEmail ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} />}
                  {sendingEmail ? 'Enviando...' : 'Enviar al Cliente'}
                </button>
              </>
            )}
            {invoice.status === 'Sent' && (
              <>
                <button
                  onClick={handleMarkPaid}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  data-testid="mark-paid-button"
                >
                  <CheckCircle size={18} />
                  Marcar Pagada
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  data-testid="resend-invoice-email-button"
                >
                  {sendingEmail ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} />}
                  Reenviar
                </button>
              </>
            )}
            {['Draft', 'Sent'].includes(invoice.status) && (
              <button
                onClick={handleCancel}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                data-testid="cancel-invoice-button"
              >
                <XCircle size={18} />
                Cancelar
              </button>
            )}
            <button
              onClick={handleDownloadPdf}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              data-testid="download-invoice-pdf-button"
            >
              <Download size={18} />
              PDF
            </button>
            {invoice.status === 'Draft' && (
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                data-testid="delete-invoice-button"
              >
                <Trash size={18} />
              </button>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-6">
          {/* Create from Quote option (only for new) */}
          {isNew && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <h3 className="font-semibold text-amber-800 mb-3">Crear desde Cotizacion</h3>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="form-label text-amber-700">Seleccionar cotizacion aprobada</label>
                  <select
                    value={selectedQuoteId}
                    onChange={(e) => setSelectedQuoteId(e.target.value)}
                    className="form-input"
                    data-testid="select-quote-for-invoice"
                  >
                    <option value="">-- Seleccionar Cotizacion --</option>
                    {quotes.map(q => (
                      <option key={q.id} value={q.id}>
                        {q.quote_number} - {q.client_name} - ${q.total?.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleCreateFromQuote}
                  disabled={!selectedQuoteId || saving}
                  className="px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 font-medium"
                  data-testid="create-from-quote-button"
                >
                  {saving ? <CircleNotch size={18} className="animate-spin" /> : 'Crear Factura'}
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-amber-200">
                <p className="text-sm text-amber-600">O crea una factura manual abajo:</p>
              </div>
            </div>
          )}

          {/* Manual Form */}
          <form onSubmit={handleSave} className="space-y-6">
            {/* Client Selection */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Cliente</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Seleccionar Cliente *</label>
                  <select
                    value={invoice.client_id}
                    onChange={(e) => setInvoice({...invoice, client_id: e.target.value})}
                    className="form-input"
                    required
                    data-testid="invoice-client-select"
                  >
                    <option value="">Seleccionar...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} {client.company ? `(${client.company})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Idioma</label>
                  <select
                    value={invoice.language}
                    onChange={(e) => setInvoice({...invoice, language: e.target.value})}
                    className="form-input"
                    data-testid="invoice-language-select"
                  >
                    <option value="ES">Espanol</option>
                    <option value="EN">English</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Dias de Vencimiento</label>
                  <input
                    type="number"
                    value={invoice.due_days}
                    onChange={(e) => setInvoice({...invoice, due_days: parseInt(e.target.value) || 30})}
                    className="form-input"
                    min="1"
                    data-testid="invoice-due-days-input"
                  />
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Servicios / Items</h2>
                <button
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                  data-testid="add-invoice-item-button"
                >
                  <Plus size={16} />
                  Agregar
                </button>
              </div>

              <div className="space-y-4">
                {invoice.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 items-end p-4 bg-slate-50 rounded-lg">
                    <div className="col-span-12 md:col-span-5">
                      <label className="form-label">Descripcion</label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        className="form-input"
                        placeholder="Descripcion del servicio"
                        data-testid={`invoice-item-description-${index}`}
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <label className="form-label">Cantidad</label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        className="form-input"
                        min="1"
                        step="0.01"
                        data-testid={`invoice-item-quantity-${index}`}
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <label className="form-label">Precio Unit.</label>
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                        className="form-input"
                        min="0"
                        step="0.01"
                        data-testid={`invoice-item-price-${index}`}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-2">
                      <label className="form-label">Total</label>
                      <p className="form-input bg-slate-100">
                        ${calculateItemTotal(item).toLocaleString()}
                      </p>
                    </div>
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        disabled={invoice.items.length === 1}
                        data-testid={`remove-invoice-item-${index}`}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Subtotal:</span>
                      <span className="font-medium">${calculateSubtotal().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Tax (%):</span>
                      <input
                        type="number"
                        value={invoice.tax_percent}
                        onChange={(e) => setInvoice({...invoice, tax_percent: parseFloat(e.target.value) || 0})}
                        className="w-20 px-2 py-1 border border-slate-200 rounded text-right"
                        min="0"
                        step="0.01"
                        data-testid="invoice-tax-input"
                      />
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t">
                      <span>Total:</span>
                      <span className="text-blue-600">${calculateTotal().toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Terms & Notes */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Terminos de Pago</label>
                  <select
                    value={invoice.payment_terms}
                    onChange={(e) => setInvoice({...invoice, payment_terms: e.target.value})}
                    className="form-input"
                    data-testid="invoice-payment-terms-select"
                  >
                    {PAYMENT_TERMS_OPTIONS.map(term => (
                      <option key={term} value={term}>{term}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Notas adicionales</label>
                  <textarea
                    value={invoice.notes || ''}
                    onChange={(e) => setInvoice({...invoice, notes: e.target.value})}
                    className="form-input"
                    rows={3}
                    data-testid="invoice-notes-input"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => isNew ? navigate('/invoices') : setEditing(false)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="cancel-edit-button"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                data-testid="save-invoice-button"
              >
                {saving && <CircleNotch size={18} className="animate-spin" />}
                {saving ? 'Guardando...' : 'Guardar Factura'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        /* View Mode */
        <div className="space-y-6">
          {/* Invoice Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-slate-500">Cliente</p>
                <Link to={`/clients/${invoice.client_id}`} className="font-medium text-blue-600 hover:text-blue-700">
                  {invoice.client_name}
                </Link>
                <p className="text-sm text-slate-500">{invoice.client_email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Fecha de Creacion</p>
                <p className="font-medium">
                  {new Date(invoice.created_at).toLocaleDateString('es-ES', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Fecha de Vencimiento</p>
                <p className="font-medium">
                  {new Date(invoice.due_date).toLocaleDateString('es-ES', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
              </div>
              {invoice.quote_number && (
                <div>
                  <p className="text-sm text-slate-500">Cotizacion Ref.</p>
                  <p className="font-medium text-blue-600">{invoice.quote_number}</p>
                </div>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold">Servicios</h2>
            </div>
            <table className="w-full" data-testid="invoice-items-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase text-slate-500">Descripcion</th>
                  <th className="px-6 py-3 text-center text-xs font-bold uppercase text-slate-500">Cantidad</th>
                  <th className="px-6 py-3 text-right text-xs font-bold uppercase text-slate-500">Precio Unit.</th>
                  <th className="px-6 py-3 text-right text-xs font-bold uppercase text-slate-500">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoice.items?.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4">{item.description}</td>
                    <td className="px-6 py-4 text-center">{item.quantity}</td>
                    <td className="px-6 py-4 text-right">${item.unit_price?.toLocaleString()}</td>
                    <td className="px-6 py-4 text-right font-medium">${item.total?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50">
                <tr>
                  <td colSpan="3" className="px-6 py-3 text-right text-sm text-slate-600">Subtotal:</td>
                  <td className="px-6 py-3 text-right font-medium">${invoice.subtotal?.toLocaleString()}</td>
                </tr>
                <tr>
                  <td colSpan="3" className="px-6 py-3 text-right text-sm text-slate-600">Tax ({invoice.tax_percent}%):</td>
                  <td className="px-6 py-3 text-right font-medium">${invoice.tax_amount?.toLocaleString()}</td>
                </tr>
                <tr className="bg-blue-50">
                  <td colSpan="3" className="px-6 py-4 text-right text-lg font-bold">Total:</td>
                  <td className="px-6 py-4 text-right text-lg font-bold text-blue-600" data-testid="invoice-total">${invoice.total?.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Payment & Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold mb-3">Terminos de Pago</h3>
              <p className="text-slate-600">{invoice.payment_terms}</p>
            </div>
            {invoice.notes && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold mb-3">Notas</h3>
                <p className="text-slate-600 whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}
          </div>

          {/* Payment status */}
          {invoice.status === 'Paid' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <CurrencyDollar size={28} className="text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-800">Factura Pagada</h3>
                  <p className="text-green-700">
                    Monto: ${invoice.amount_paid?.toLocaleString()}
                    {invoice.paid_at && ` - ${new Date(invoice.paid_at).toLocaleString('es-ES')}`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {invoice.status === 'Cancelled' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <XCircle size={28} className="text-red-600" />
                <div>
                  <h3 className="font-semibold text-red-800">Factura Cancelada</h3>
                  {invoice.cancelled_at && (
                    <p className="text-red-700">
                      Cancelada el {new Date(invoice.cancelled_at).toLocaleString('es-ES')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
